import React from "react";

/**
 * Coffee Belt Map — uses the public-domain Wikimedia Commons coffee map (CC0)
 * with country-name labels overlaid in EN + AR.
 *
 * Legend:
 *   • Green  → Arabica
 *   • Red    → Robusta
 *   • Yellow → Arabica & Robusta
 *
 * Coordinates are percent-based on the full original image (1944×986).
 */
export default function CoffeeBeltMap({ className = "", caption = "", isRTL = false }) {
  // Calibrated positions on the full Wikimedia Coffee_map.png (visible map area only).
  // x, y are percentages — measured against the entire image, not the cropped one.
  const labels = [
    // ===== Americas =====
    { x: 17.5, y: 38.5, en: "Mexico",        ar: "المكسيك",        type: "both" },
    { x: 20.0, y: 47.0, en: "Guatemala",     ar: "غواتيمالا",      type: "both" },
    { x: 20.5, y: 49.5, en: "Honduras",      ar: "هندوراس",        type: "arabica" },
    { x: 21.5, y: 52.0, en: "Nicaragua",     ar: "نيكاراغوا",      type: "arabica" },
    { x: 22.0, y: 54.5, en: "Costa Rica",    ar: "كوستاريكا",      type: "arabica" },
    { x: 24.0, y: 56.5, en: "Panama",        ar: "بنما",            type: "arabica" },
    { x: 26.5, y: 58.5, en: "Colombia",      ar: "كولومبيا",       type: "arabica" },
    { x: 26.0, y: 64.0, en: "Ecuador",       ar: "الإكوادور",       type: "both" },
    { x: 28.5, y: 70.5, en: "Peru",          ar: "البيرو",          type: "arabica" },
    { x: 33.5, y: 68.0, en: "Brazil",        ar: "البرازيل",        type: "both" },
    { x: 30.0, y: 75.0, en: "Bolivia",       ar: "بوليفيا",         type: "arabica" },
    // ===== Africa =====
    { x: 47.0, y: 49.0, en: "Côte d’Ivoire", ar: "ساحل العاج",     type: "robusta" },
    { x: 50.5, y: 51.5, en: "Cameroon",      ar: "الكاميرون",       type: "both" },
    { x: 57.0, y: 51.0, en: "Ethiopia",      ar: "إثيوبيا",         type: "arabica" },
    { x: 56.5, y: 56.0, en: "Uganda",        ar: "أوغندا",          type: "both" },
    { x: 58.0, y: 58.0, en: "Kenya",         ar: "كينيا",           type: "arabica" },
    { x: 57.5, y: 62.0, en: "Tanzania",      ar: "تنزانيا",         type: "both" },
    { x: 55.5, y: 59.0, en: "Rwanda",        ar: "رواندا",          type: "arabica" },
    { x: 55.8, y: 60.5, en: "Burundi",       ar: "بوروندي",         type: "arabica" },
    { x: 53.0, y: 58.0, en: "DR Congo",      ar: "الكونغو",         type: "robusta" },
    // ===== Middle East =====
    { x: 60.0, y: 49.5, en: "Yemen",         ar: "اليمن",           type: "arabica" },
    // ===== Asia =====
    { x: 70.0, y: 52.0, en: "India",         ar: "الهند",           type: "both" },
    { x: 80.5, y: 53.0, en: "Vietnam",       ar: "فيتنام",          type: "robusta" },
    { x: 82.5, y: 60.5, en: "Indonesia",     ar: "إندونيسيا",       type: "both" },
    { x: 84.0, y: 49.5, en: "Philippines",   ar: "الفلبين",         type: "both" },
    { x: 79.5, y: 56.5, en: "Thailand",      ar: "تايلاند",         type: "both" },
  ];

  const typeColor = {
    arabica: "#16a34a",
    robusta: "#dc2626",
    both:    "#ca8a04",
  };

  return (
    <div className={`relative rounded-2xl overflow-hidden border border-[#2A2421]/10 bg-white ${className}`} data-testid="coffee-belt-map">
      <div className="relative w-full" style={{ aspectRatio: "1944 / 986" }}>
        {/* Base map: CC0 Wikimedia Commons (https://commons.wikimedia.org/wiki/File:Coffee_map.png) */}
        <img
          src="https://upload.wikimedia.org/wikipedia/commons/thumb/7/7d/Coffee_map.png/1280px-Coffee_map.png"
          alt="Coffee producing countries map"
          className="absolute inset-0 w-full h-full object-contain"
          referrerPolicy="no-referrer"
        />

        {/* White overlays covering the original French legend (bottom strip + left-side legend box) on the source image */}
        <div className="absolute inset-x-0 bottom-0 h-[12%] bg-white" />
        <div className="absolute bottom-[10%] left-0 w-[26%] h-[38%] bg-white" />

        {/* Country labels overlay (positioned relative to the full image area) */}
        <div className="absolute inset-0 pointer-events-none">
          {labels.map((l, i) => (
            <div
              key={i}
              className="absolute -translate-x-1/2 -translate-y-1/2 flex flex-col items-center gap-0.5"
              style={{ left: `${l.x}%`, top: `${l.y}%` }}
            >
              <span
                className="w-1.5 h-1.5 rounded-full ring-2 ring-white shadow"
                style={{ backgroundColor: typeColor[l.type] }}
              />
              <span
                className="px-1.5 py-[1px] rounded-md bg-white/95 backdrop-blur-sm text-[9px] sm:text-[10px] md:text-[11px] font-semibold text-[#2A2421] whitespace-nowrap shadow-sm border border-[#2A2421]/10"
                dir={isRTL ? "rtl" : "ltr"}
              >
                {isRTL ? l.ar : l.en}
              </span>
            </div>
          ))}
        </div>

        {/* Caption pill */}
        {caption && (
          <div className="absolute top-4 start-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#2A2421]/90 backdrop-blur-sm text-[#F9F6F0] text-xs font-medium">
            {caption}
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center justify-center gap-5 px-4 py-3 border-t border-[#2A2421]/10 bg-[#F9F6F0] text-xs">
        <LegendDot color="#16a34a" label={isRTL ? "أرابيكا" : "Arabica"} />
        <LegendDot color="#dc2626" label={isRTL ? "روبوستا" : "Robusta"} />
        <LegendDot color="#ca8a04" label={isRTL ? "أرابيكا وروبوستا" : "Arabica & Robusta"} />
      </div>
    </div>
  );
}

const LegendDot = ({ color, label }) => (
  <span className="inline-flex items-center gap-1.5">
    <span className="w-3 h-3 rounded-sm" style={{ backgroundColor: color }} />
    <span className="text-[#2A2421] font-medium">{label}</span>
  </span>
);
